import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ApiClientCredential } from '../../models/appcloud/api-client-credential.model';
import { ErrorHandlingService } from '../error-handling';
import { HttpHeaderConstants } from '../http-header.constant';
import { SpinnerService } from '../spinner';

Injectable({
    providedIn: 'root',
});
export class ApiClientCredentialService {
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private API_CLIENT_CREDENTIAL_URL: string = environment.publicApiHost + '/appcloud/api-client-credentials';

    create(apiClientCredential: ApiClientCredential, additionalHeaders: any = {}): Observable<ApiClientCredential> {
        const process = 'ApiClientCredentialService.create';
        this.#spinnerService.addProcess(process, 'Creating your api client credential...');

        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http
            .post<ApiClientCredential>(this.API_CLIENT_CREDENTIAL_URL, apiClientCredential, httpOptions)
            .pipe(
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while saving your credential. Please try again later.'
                    );
                    return of<ApiClientCredential>();
                }),
                finalize(() => {
                    this.#spinnerService.completeProcess(process);
                })
            );
    }

    update(
        id: number,
        apiClientCredential: ApiClientCredential,
        additionalHeaders: any = {}
    ): Observable<ApiClientCredential> {
        const process = 'ApiClientCredentialService.update';
        this.#spinnerService.addProcess(process, 'Updating your api client credential...');

        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.mergePatch,
                ...additionalHeaders,
            }),
        };

        return this.#http
            .patch<ApiClientCredential>(this.API_CLIENT_CREDENTIAL_URL + '/' + id, apiClientCredential, httpOptions)
            .pipe(
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while updating your credential. Please try again later.'
                    );
                    return of<ApiClientCredential>();
                }),
                finalize(() => {
                    this.#spinnerService.completeProcess(process);
                })
            );
    }

    get(id: number, additionalHeaders: any = {}): Observable<ApiClientCredential> {
        const process = 'ApiClientCredentialService.getById.' + id;
        this.#spinnerService.addProcess(process, 'Loading your api client credential...');

        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http.get<ApiClientCredential>(`${this.API_CLIENT_CREDENTIAL_URL}/${id}`, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving api client credential data from Relationship One. Please try again later.'
                );
                return of<ApiClientCredential>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    delete(id: number, additionalHeaders: any = {}): Observable<any> {
        const process = 'ApiClientCredentialService.delete.' + id;
        this.#spinnerService.addProcess(process, 'Deleting your api client credential...');

        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http.delete<Observable<any>>(`${this.API_CLIENT_CREDENTIAL_URL}/${id}`, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while deleting your api client credential. Try again later.'
                );
                return of<Observable<any>>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }
}
